import BlockBookingEditor from './BlockBookingEditor';
import DayCourtScopePicker from './DayCourtScopePicker';
import { getBookingSettings, findBlockOverlaps } from '../utils/bookingRulesUtil';
import {
  MIN_MIN_BOOKING_HOURS,
  MAX_MIN_BOOKING_HOURS,
  DEFAULT_BLOCK_BOOKING_START_HOUR,
  DEFAULT_BLOCK_BOOKING_END_HOUR,
} from '../constants/bookingConst';
import '../css/BookingSettingsTab.css';

const MIN_HOUR_OPTIONS = Array.from(
  { length: MAX_MIN_BOOKING_HOURS - MIN_MIN_BOOKING_HOURS + 1 },
  (_, i) => MIN_MIN_BOOKING_HOURS + i,
);

/**
 * Config -> Booking Settings: the rules a guest selection must meet before
 * Reserve unlocks, and the block bookings that sell a run of hours as one.
 *
 * Edits the raw `bookingSettings` object rather than the normalised one —
 * normalising mid-edit would drop a block the moment its end hour was
 * briefly before its start, which is exactly when the admin is fixing it.
 * Everything that READS these settings goes through getBookingSettings.
 *
 * @param {object} props
 * @param {object|undefined} props.value - formData.bookingSettings
 * @param {(next: object) => void} props.onChange - receives the whole object
 * @param {Array<{number: number, label: string}>} props.courtOptions - every configured court
 * @returns {JSX.Element}
 */
function BookingSettingsTab({ value, onChange, courtOptions }) {
  const raw = value || {};
  const settings = getBookingSettings({ bookingSettings: raw });
  // The raw list keeps half-edited blocks on screen; see the note above.
  const blocks = Array.isArray(raw.blockBookings)
    ? raw.blockBookings.filter(Boolean)
    : Object.values(raw.blockBookings || {}).filter(Boolean);

  // A handful of blocks at most, so this is recomputed each render rather
  // than memoised against a list that is rebuilt every render anyway.
  const overlappingIds = new Set(findBlockOverlaps(blocks).flat());

  /** @param {object} patch - top-level fields to merge into bookingSettings */
  const update = (patch) => onChange({ ...raw, ...patch });

  /** @param {object[]} next */
  const setBlocks = (next) => update({ blockBookings: next });

  const handleAddBlock = () => setBlocks([...blocks, {
    id: `block-${Date.now()}`,
    label: '',
    startHour: DEFAULT_BLOCK_BOOKING_START_HOUR,
    endHour: DEFAULT_BLOCK_BOOKING_END_HOUR,
    days: [],
    courts: [],
  }]);

  return (
    <div className="config-section bks-root">
      <h3 className="bks-title">Booking Rules</h3>
      <p className="bks-sub">
        What a guest&rsquo;s selection has to meet before the Reserve button unlocks. Admin bookings from Manage
        availability are not held to these rules.
      </p>

      <div className="bks-card">
        <label className="bks-field bks-field-inline">
          <span className="bks-card-title">Minimum booking hours</span>
          <select
            value={settings.minBookingHours}
            onChange={(e) => update({ minBookingHours: Number(e.target.value) })}
          >
            {MIN_HOUR_OPTIONS.map(h => (
              <option key={h} value={h}>{h} hour{h === 1 ? '' : 's'}</option>
            ))}
          </select>
        </label>
        <p className="bks-hint">
          The least a guest can book &mdash; any number of hours above it is fine. Below it, Reserve stays disabled
          and the guest is told how many more hours to add.
        </p>

        {/* Where the minimum (and the connecting rules below) apply. Hours
            on other days or courts are left alone — they can be booked one
            at a time and don't count toward the minimum. */}
        <div className="bks-card-section">
          <span className="bks-card-title">Applies to</span>
          <span className="bks-hint">
            Leave everything ticked to apply on every day and court. Hours on days or courts left unticked can be
            booked freely and don&rsquo;t count toward the minimum.
          </span>
          <DayCourtScopePicker
            days={raw.ruleDays}
            courts={raw.ruleCourts}
            courtOptions={courtOptions}
            onChange={(patch) => update({
              ...('days' in patch ? { ruleDays: patch.days } : {}),
              ...('courts' in patch ? { ruleCourts: patch.courts } : {}),
            })}
          />
        </div>

        {/* How those hours may be spread out — part of the same rule, so it
            sits in the same card rather than as a setting of its own. */}
        <div className="bks-card-section">
          <label className="bks-toggle">
            <input
              type="checkbox"
              checked={settings.allowStaggered}
              onChange={(e) => update({ allowStaggered: e.target.checked })}
            />
            <span>
              <span className="bks-card-title">Allow staggered hours</span>
              <span className="bks-hint">
                Any hours on any courts and dates, as long as they add up to the minimum &mdash; e.g. Court 1 at
                6&ndash;7 PM and Court 2 at 9&ndash;10 PM is 2 hours. Uncheck to require connecting hours on each
                date (6&ndash;7 PM then 7&ndash;8 PM, no gaps).
              </span>
            </span>
          </label>

          {!settings.allowStaggered && (
            <div className="bks-nested">
              <span className="bks-card-title">Connecting hours</span>
              <label className="bks-radio">
                <input
                  type="radio"
                  name="bks-connecting-court"
                  checked={!settings.connectingSameCourtOnly}
                  onChange={() => update({ connectingSameCourtOnly: false })}
                />
                <span>
                  <strong>Different courts allowed</strong>
                  <span className="bks-hint">
                    Any courts, as long as the day&rsquo;s hours add up to the minimum &mdash; e.g. Court 1 and Court 2
                    at 4&ndash;5 PM, or Court 1 at 6&ndash;7 PM then Court 2 at 7&ndash;8 PM.
                  </span>
                </span>
              </label>
              <label className="bks-radio">
                <input
                  type="radio"
                  name="bks-connecting-court"
                  checked={settings.connectingSameCourtOnly}
                  onChange={() => update({ connectingSameCourtOnly: true })}
                />
                <span>
                  <strong>Same court only</strong>
                  <span className="bks-hint">
                    Every court booked needs at least the minimum on its own, in connecting hours &mdash; e.g. with a
                    2-hour minimum, Court 1 6&ndash;8 PM and Court 2 6&ndash;8 PM is fine, but one hour on Court 2 is
                    not.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>
      </div>

      <h3 className="bks-title bks-title-gap">Block Bookings</h3>
      <p className="bks-sub">
        Sell a range of hours as one booking, e.g. 5&ndash;10 PM. Guests see the block as a single button and
        book every hour in it together; Manage availability shows it as one cell too. Leave Days or Courts
        all ticked to apply everywhere.
      </p>

      {blocks.length === 0 && <p className="bks-empty">No block bookings yet.</p>}
      {blocks.map((block, index) => (
        <BlockBookingEditor
          key={block.id || index}
          block={block}
          courtOptions={courtOptions}
          overlaps={overlappingIds.has(block.id)}
          onChange={(patch) => setBlocks(blocks.map(b => (b === block ? { ...b, ...patch } : b)))}
          onRemove={() => setBlocks(blocks.filter(b => b !== block))}
        />
      ))}

      <button type="button" className="bks-add-btn" onClick={handleAddBlock}>
        + Add block booking
      </button>
    </div>
  );
}

export default BookingSettingsTab;
